/**
 * Stars — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const starsPack: ToilePack = {
    slug: 'stars',
    category: 'motif',
    name: 'Stars',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 832},
    readings: [
        {key: 'single', label: 'Five-point star', subject: 'A single five-point star centred on a plain ground with a small sparkle at one tip, nothing else.'},
        {key: 'scatter', label: 'Star scatter', subject: 'A scatter of stars in many sizes across a deep blue ground — five-point stars, four-point sparkles, tiny dots — a few clustered, one larger at the centre.'},
        {key: 'shooting', label: 'Shooting star', subject: 'A shooting star arcing across a plain dark ground with a long curving trail of sparkles behind it, a crescent moon small in the corner.'},
    ],
    styles: [
        {key: 'flat', label: 'Flat', style: 'flat vector illustration, bold simplified shapes, limited palette, plain ground', palette: 'a single bold gold on white, no gradients', framing: 'centred on a plain white background with generous padding around the shape'},
        {key: 'tattoo', label: 'Tattoo linework', style: 'fine-line tattoo design, black ink only, clean weighted outlines, dotwork shading, white ground', palette: 'black ink only, no other colour', framing: 'centred on a plain white background with a clear margin around the design'},
        {key: 'gold', label: 'Gold foil', style: 'gold-foil print on deep navy, metallic shimmer, matte ground', palette: 'warm metallic gold on deep navy, no other colour', framing: 'set on a deep navy background with a thin metallic border'},
    ],
    directions: ['a pair of stars', 'in rainbow colours', 'for a nursery mobile'],
    directionHint: 'try: a pair of stars',
    seo: {
        title: 'Star art: single, scatter, shooting in three styles',
        metaDescription: 'Nine star illustrations — a single star, a scatter and a shooting star — in flat, tattoo linework and gold-foil styles for nursery to tattoo.',
        h1: 'Star drawings: single, scatter, shooting star in three styles',
        tagline: 'Nine renders, three star readings, three looks each — nursery to gold foil.',
        intro: 'The shape itself, not the galaxy or moon around it: a single five-point star, a scatter of stars in many sizes, and a shooting star with a curving trail. Each renders flat and bold, as fine tattoo linework, or in metallic gold foil on navy. Ask for a pair of stars or rainbow colours and the same three looks carry it.',
        useCases: [
            'a nursery mobile or wall print',
            'a tattoo flash reference sheet',
            'a gift card or sticker design',
            'a social media graphic',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed as a clean square crop, suited to a print, a card or a tattoo reference.'},
            {q: 'Can I get a pair of stars or rainbow colours?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Flat is a single bold gold shape on white, tattoo linework is black-ink dotwork shading, and gold foil is metallic shimmer on deep navy.'},
            {q: 'Is this a real photograph of the night sky?', a: 'No — every image is an illustration, drawn in the chosen style, not a photo.'},
        ],
    },
}
